// MobileSearchTab — Suche-Reiter des mobilen Hinzufügen-Modus: Trefferliste über den
// gesamten Kartenbestand, Kandidaten-Auswahl per Langdruck, gedockte Filterleiste (mit
// Kandidaten-Leiste darüber) und Viewer über den Treffern.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RefreshCw, Search, SearchX, X } from 'lucide-react';
import CardGrid from '../../../../ui/CardGrid';
import EmptyState from '../../../../ui/EmptyState';
import Skeleton from '../../../../ui/Skeleton';
import { useToast } from '../../../../ui/Toast';
import { errorMessage } from '../../../../api/errorMessage';
import { formatCardCount } from '../hooks/useMobileDeckActions';
import { useCardSelection } from '../hooks/useCardSelection';
import { useHistoryLayer } from '../hooks/useHistoryLayer';
import { useCloseOnBack } from '../../../../ui/backLayer/useCloseOnBack';
import MobileResultTile from './MobileResultTile';
import AddResultsViewer from './AddResultsViewer';
import { useCardBrowserSearch } from '../../hooks/useCardBrowserSearch';
import CardBrowserFilterRow from '../../components/CardBrowserFilterRow';
import { useLoadMoreSentinel } from '../../../../hooks/useLoadMoreSentinel';

/**
 * @param {object[]} deckCards — Karten des Decks, für den „✓ im Deck"-Abgleich per oracleId
 * @param {boolean} canEditDeck — Plus-Knöpfe, Viewer-Knopf und Kandidaten-Auswahl
 * @param {object} filters — Filter-Objekt; liegt im Rahmen, weil `Tabs.Panel` den inaktiven
 *   Reiter abbaut und sein State nach jedem Wechsel weg wäre
 * @param {(patch: object) => void} onFiltersChange — Teil-Änderung des Filter-Objekts
 * @param {(entry, options) => Promise<{entryId?: number, duplicate: boolean}|null>} addEntry
 *   — gemeinsamer Add-Pfad der Page (Toasts, Undo-Registrierung, Ziel-Handhabung)
 * @param {string|number} targetCategoryId — aktueller Wähler-Wert (`'auto'` oder ID);
 *   die Kandidaten-Schleife friert ihn beim Start ein
 * @param {() => void} onResetTarget — setzt den Wähler auf „Automatisch" zurück
 * @param {(cardId) => void} zeigeKarteImDeck — springt zur genannten Karte im Deck-Zweig;
 *   Ziel des antippbaren Sammel-Toasts (erste erfolgreich hinzugefügte Karte des Batches)
 */
export default function MobileSearchTab({
  deckId,
  deckCards = [],
  canEditDeck = false,
  filters,
  onFiltersChange,
  addEntry,
  targetCategoryId = 'auto',
  onResetTarget,
  undo,
  zeigeKarteImDeck,
}) {
  const { push: pushToast } = useToast();

  const {
    entries,
    totalFilteredCards,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    refetch,
    isEmptyQuery,
    filterToken,
  } = useCardBrowserSearch({ filters });

  const candidates = useCardSelection(deckId);
  // Zurück beendet erst die Kandidaten-Auswahl, der nächste Druck schließt die Add-Ebene.
  useCloseOnBack(candidates.active, candidates.clear);
  const {
    isOpen: isViewerOpen,
    payload: viewerPayload,
    open: openViewer,
    close: closeViewer,
  } = useHistoryLayer('add-viewer');

  const deckOracleIds = useMemo(
    () => new Set((deckCards || []).map((card) => card.oracleId).filter(Boolean)),
    [deckCards]
  );

  // ── Kandidaten-Auswahl ───────────────────────────────────────────────────
  // Ein Filterwechsel tauscht die Trefferliste komplett aus; Markierungen auf nicht mehr
  // sichtbaren Karten wären unsichtbare Nebenwirkungen der nächsten Sammel-Aktion.
  const lastFilterTokenRef = useRef(filterToken);
  useEffect(() => {
    if (lastFilterTokenRef.current === filterToken) return;
    lastFilterTokenRef.current = filterToken;
    if (!candidates.active) return;
    if (candidates.count > 0) {
      pushToast({ tone: 'info', message: `Auswahl (${candidates.count}) verworfen` });
    }
    candidates.clear();
  }, [filterToken, candidates, pushToast]);

  const handleTap = useCallback((entry) => {
    if (candidates.active) {
      candidates.toggle(entry.id);
      return;
    }
    const index = entries.findIndex((e) => e.id === entry.id);
    openViewer({ index: index < 0 ? 0 : index });
  }, [candidates, entries, openViewer]);

  const handleLongPress = useCallback((entry) => {
    if (candidates.active) candidates.toggle(entry.id);
    else candidates.start(entry.id);
  }, [candidates]);

  const handleAddCandidates = useCallback(async () => {
    const selected = entries.filter((entry) => candidates.ids.has(entry.id));
    // Das Ziel EINMAL beim Start festhalten: ein Fehlschlag mitten im Batch darf den
    // Wähler nicht kippen, sonst landeten die restlichen Karten still in der
    // Typ-Fallback-Kategorie.
    const target = targetCategoryId;
    candidates.clear();
    if (selected.length === 0) return;

    const entryIds = [];
    let added = 0;
    let duplicates = 0;
    let failed = 0;
    // Ziel des Sammel-Toast-Tipps: die erste erfolgreich hinzugefügte Karte. Die
    // Schleifenvariable trägt dieselbe Kennung, mit der die Hervorhebung im Raster
    // vergleicht — die Rückgabe des Add-Pfads liefert stattdessen Undo-Kennungen.
    let firstAddedCardId = null;
    for (const entry of selected) {
      const result = await addEntry(entry, { silent: true, target });
      if (result?.duplicate) duplicates += 1;
      else if (result) {
        added += 1;
        if (firstAddedCardId == null) firstAddedCardId = entry.id;
        if (result.entryId != null) entryIds.push(result.entryId);
      } else failed += 1;
    }

    if (added > 0) {
      const duplicateNote = duplicates > 0
        ? `, ${formatCardCount(duplicates)} ${duplicates === 1 ? 'war' : 'waren'} bereits im Deck`
        : '';
      pushToast({
        tone: 'success',
        message: `${formatCardCount(added)} hinzugefügt${duplicateNote}`,
        action: entryIds.length > 0 ? {
          label: 'Rückgängig',
          onClick: async () => {
            // Sequenziell: der Busy-Wächter von useDeckUndo lässt nur eine
            // Gegenaktion zur Zeit zu und liefert sonst undefined zurück.
            for (const entryId of entryIds) {
              await undo.undoEntry(entryId);
            }
          },
        } : undefined,
        onMessageClick: () => zeigeKarteImDeck(firstAddedCardId),
      });
    } else if (duplicates > 0) {
      pushToast({
        tone: 'info',
        message: `${formatCardCount(duplicates)} ${duplicates === 1 ? 'war' : 'waren'} bereits im Deck`,
      });
    }

    // Ein toter Zielwert (z.B. inzwischen gelöschte Kategorie) darf nicht kleben
    // bleiben — der Grund des Fehlschlags ist aus der null-Rückgabe nicht ablesbar.
    if (failed > 0 && target !== 'auto') onResetTarget?.();
  }, [entries, candidates, targetCategoryId, addEntry, pushToast, undo, onResetTarget, zeigeKarteImDeck]);

  // ── Hinzufügen aus Kachel und Viewer ─────────────────────────────────────
  // Solange ein Add läuft, löst ein weiteres Plus nichts aus: der zweite Tipp eines
  // Doppeltipps endete sonst als Duplikat-Toast. Der Ref sperrt schon im selben Tick, der
  // State färbt die Kachel.
  const [addingId, setAddingId] = useState(null);
  const addingRef = useRef(null);
  // Vor/Zurück zwischen zwei Editor-Adressen lässt diese Komponente stehen; ein Add des
  // vorigen Decks darf das neue nicht sperren.
  useEffect(() => {
    addingRef.current = null;
    setAddingId(null);
  }, [deckId]);

  const handleAdd = useCallback(async (entry) => {
    if (addingRef.current !== null) return;
    addingRef.current = entry.id;
    setAddingId(entry.id);
    try {
      await addEntry(entry);
    } finally {
      if (addingRef.current === entry.id) {
        addingRef.current = null;
        setAddingId(null);
      }
    }
  }, [addEntry]);

  const sentinelRef = useLoadMoreSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });

  const candidateBar = candidates.active ? (
    <div className="mobile-add-candidates">
      <span className="mobile-add-candidates__count">{candidates.count} ausgewählt</span>
      <button
        type="button"
        className="btn-secondary mobile-add-candidates__action"
        disabled={candidates.count === 0}
        onClick={handleAddCandidates}
      >
        <Plus size={16} aria-hidden /> hinzufügen
      </button>
      <button
        type="button"
        className="btn-icon mobile-add-candidates__cancel"
        aria-label="Auswahl beenden"
        onClick={candidates.clear}
      >
        <X aria-hidden />
      </button>
    </div>
  ) : null;

  return (
    <>
      {isEmptyQuery ? (
        <EmptyState
          icon={Search}
          title="Tippe einen Kartennamen"
          description="Oder wähle nur Filter — auch das bringt schon Treffer."
        />
      ) : status === 'error' ? (
        <EmptyState
          variant="error"
          icon={RefreshCw}
          title="Karten konnten nicht geladen werden"
          description={errorMessage(error, 'Bitte später erneut versuchen.')}
          onRetry={() => refetch()}
        />
      ) : status === 'pending' ? (
        <CardGrid variant="touch">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height="180px" />
          ))}
        </CardGrid>
      ) : entries.length === 0 ? (
        <EmptyState
          variant="filter"
          icon={SearchX}
          title="Keine Karten gefunden"
          description="Passe Suche oder Filter an."
        />
      ) : (
        <>
          <CardGrid variant="touch">
            {entries.map((entry) => (
              <MobileResultTile
                key={entry.id}
                entry={entry}
                inDeck={Boolean(entry.oracleId && deckOracleIds.has(entry.oracleId))}
                selected={candidates.ids.has(entry.id)}
                canAdd={canEditDeck && !candidates.active}
                busy={addingId === entry.id}
                onTap={handleTap}
                onLongPress={canEditDeck ? handleLongPress : undefined}
                onAdd={handleAdd}
              />
            ))}
          </CardGrid>
          <div ref={sentinelRef} aria-hidden />
          <div className="mobile-add-footer">
            <span>{entries.length} von {totalFilteredCards}</span>
            {isFetchingNextPage && <Skeleton width="120px" height="12px" radius="sm" />}
          </div>
        </>
      )}

      <CardBrowserFilterRow
        filters={filters}
        onChange={onFiltersChange}
        showGrouping
        showOwnedOnly
        dockBar={candidateBar}
      />

      <AddResultsViewer
        isOpen={isViewerOpen}
        payloadIndex={viewerPayload?.index}
        onClose={closeViewer}
        entries={entries}
        status={status}
        canAdd={canEditDeck}
        onAdd={handleAdd}
        deckOracleIds={deckOracleIds}
      />
    </>
  );
}
